import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  output,
} from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { QuickSearchVehicleItem } from '@fc-search/models/quick-search';
import { QuickSearchFacadeService } from '@fc-search/services/quick-search-facade.service';
import { EntityAvatarComponent } from '@fc-shared/ui/avatar/entity-avatar.component';
import { ButtonComponent } from '@fc-shared/ui/buttons/button.component';
import { TooltipDirective } from '@fc-shared/ui/tooltips/tooltip.directive';

@Component({
  selector: 'fc-qs-vehicle-result',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterModule,
    EntityAvatarComponent,
    ButtonComponent,
    TooltipDirective,
  ],
  template: `
    <div
      class="flex gap-12 items-center max-w-full relative py-8 pl-14 pr-16 hover:bg-secondary-50 cursor-pointer group border-l-2"
      [class.border-white]="!selected()"
      [class.border-primary-600]="selected()"
      [class.bg-secondary-50]="selected()"
      [routerLink]="['/vehicles/main/vehicles', result().obj.id, 'details']"
    >
      <fc-entity-avatar
        type="vehicle"
        [entity]="$any(result().obj)"
        [size]="40"
        class="shrink-0"
      />

      <div class="flex flex-col gap-4 grow min-w-0">
        <span class="text-sm font-medium text-grey-900 truncate">
          {{ result().obj.identifier }}
        </span>
        <div
          class="flex flex-wrap items-center gap-4 text-xs font-medium text-secondary-500 truncate"
        >
          <span>
            {{ result().obj.manufacturer ?? '' }} {{ result().obj.model }}
          </span>
          @if (result().obj.vin) {
            <div
              class="size-6 rounded-full bg-secondary-300 first:hidden"
            ></div>
            <span>{{ result().obj.vin }}</span>
          }
          @if (result().obj.type) {
            <div
              class="size-6 rounded-full bg-secondary-300 first:hidden"
            ></div>
            <span>{{ result().obj.type }}</span>
          }
        </div>
      </div>
      <div
        class="flex gap-4 p-4 rounded-xl bg-white border-secondary-100 shadow-sm"
        [class.opacity-0]="!selected()"
        [class.opacity-100]="selected()"
        [class.group-hover:opacity-100]="!selected()"
      >
        <div
          fcTooltip="View on map"
          [showTooltip]="selected() && activeButtonIndex() === 0"
        >
          <fc-button
            variant="text"
            color="secondary"
            [focused]="activeButtonIndex() === 0"
            icon="icon-map-outline"
            size="sm"
            iconPosition="only"
            (click)="viewOnMap(); $event.stopPropagation()"
          />
        </div>
        <div
          fcTooltip="Open in new tab"
          [showTooltip]="selected() && activeButtonIndex() === 1"
        >
          <fc-button
            variant="filled"
            color="primary"
            [focused]="activeButtonIndex() === 1"
            icon="icon-new-window"
            size="sm"
            iconPosition="only"
            (click)="viewDetails(); $event.stopPropagation()"
          />
        </div>
      </div>
    </div>
  `,
})
export class QsVehicleResultComponent {
  result = input.required<QuickSearchVehicleItem>();
  selected = input(false);
  activeButtonIndex = input(-1);
  quickAction = output();
  private router = inject(Router);
  private facade = inject(QuickSearchFacadeService);

  activateButton(index: number): void {
    switch (index) {
      case 0:
        this.viewOnMap();
        break;
      case 1:
        this.viewDetails();
        break;
    }
  }

  viewOnMap(): void {
    this.quickAction.emit();
    this.router.navigate(['/map/detail', this.result().obj.id, 'info']);
    this.facade.clearAndClose();
  }

  viewDetails(): void {
    this.quickAction.emit();
    const url = this.router.serializeUrl(
      this.router.createUrlTree([
        '/vehicles/main/vehicles/' + this.result().obj.id + '/details',
      ]),
    );
    window.open(url, '_blank');
    this.facade.clearAndClose();
  }
}
